import React, { useState } from 'react';

import { ProjectAsset } from '../../types/editor';
import AssetUploader from './AssetUploader';
import { setCurrentAssetDrag } from './dragState';

interface ProjectAssetsPanelProps {
  projectId: number;
  assets: ProjectAsset[];
  /** Called after a successful upload so the editor can append to its
   * assets list. */
  onUploaded: (asset: ProjectAsset) => void;
  /** Reorder a tile from one index to another. Local-only (visual). */
  onReorder: (fromIdx: number, toIdx: number) => void;
  /** Remove the asset from the project + timeline. */
  onDelete: (assetId: number) => void;
  /** Open in the timeline as a clip. */
  onAddClip: (asset: ProjectAsset) => void;
}

/**
 * Collapsible "Project Assets" section. Heading toggles open/closed;
 * collapsed state hides the content but doesn't unload it (so the
 * drop zone stays armed if the user opens it back up). Asset tiles
 * support drag-reorder + ↑/↓ buttons for touch / a11y, identical to
 * the wizard's UploadStep visuals.
 */
export default function ProjectAssetsPanel({
  projectId, assets, onUploaded, onReorder, onDelete, onAddClip,
}: ProjectAssetsPanelProps) {
  const [open, setOpen] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  return (
    <section className={`assets-panel${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="assets-panel-head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className={`assets-panel-chevron${open ? ' is-open' : ''}`} aria-hidden>▸</span>
        <span className="assets-panel-title">Project Assets</span>
        <span className="muted">({assets.length})</span>
      </button>

      {open && (
        <div className="assets-panel-body">
          <AssetUploader projectId={projectId} onUploaded={onUploaded} />

          {assets.length > 0 && (
            <ul
              className="wizard-upload-tiles assets-panel-tiles"
              onDragOver={(e) => e.preventDefault()}
            >
              {assets.map((a, i) => {
                const isDragging = dragIndex === i;
                const isOver = overIndex === i && dragIndex !== null && dragIndex !== i;
                return (
                  <li
                    key={a.id}
                    className={[
                      'wizard-upload-tile',
                      `kind-${a.kind}`,
                      isDragging ? 'is-dragging' : '',
                      isOver ? 'is-drop-target' : '',
                    ].filter(Boolean).join(' ')}
                    draggable
                    onDragStart={(e) => {
                      setDragIndex(i);
                      e.dataTransfer.effectAllowed = 'copyMove';
                      e.dataTransfer.setData('text/plain', String(a.id));
                      /* Custom MIME so the Timeline's drop handler can
                       * recognize a cross-component asset drag (vs.
                       * an in-panel reorder which also fires HTML5
                       * drag events). Only the type list is readable
                       * during dragover — payload (the asset id) is
                       * read on drop. */
                      e.dataTransfer.setData(
                        'application/x-megaphony-asset',
                        JSON.stringify({ id: a.id, kind: a.kind }),
                      );
                      /* Belt-and-suspenders: also stash the dragged
                       * asset in a module-level ref. Some browsers
                       * sanitize the custom MIME from
                       * dataTransfer.types during dragover, which
                       * would cause the Timeline's preventDefault to
                       * be skipped → drop canceled. Reading from the
                       * module ref instead works in every browser. */
                      setCurrentAssetDrag({ assetId: a.id, kind: a.kind });
                      /* Custom drag preview: clone the thumbnail at
                       * 50% size and offset the cursor BELOW it so the
                       * timeline's drop-cursor under the user's finger
                       * stays visible. The clone lives off-screen long
                       * enough for the browser to snapshot it as the
                       * drag image, then gets removed on the next
                       * task tick. */
                      const tile = e.currentTarget;
                      const thumb = tile.querySelector('.wizard-upload-thumb');
                      if (thumb instanceof HTMLElement) {
                        const rect = thumb.getBoundingClientRect();
                        const w = Math.max(40, Math.round(rect.width * 0.5));
                        const h = Math.max(24, Math.round(rect.height * 0.5));
                        const clone = thumb.cloneNode(true) as HTMLElement;
                        clone.style.cssText = [
                          'position: absolute',
                          'top: -9999px',
                          'left: -9999px',
                          `width: ${w}px`,
                          `height: ${h}px`,
                          'border-radius: 6px',
                          'overflow: hidden',
                          'pointer-events: none',
                          'box-shadow: 0 6px 20px rgba(0, 0, 0, 0.45)',
                        ].join(';');
                        document.body.appendChild(clone);
                        /* setDragImage offsets are FROM the cursor to
                         * the top-left of the image. (w/2, h + 24)
                         * means: cursor is 24px BELOW the preview's
                         * bottom edge, horizontally centered → the
                         * preview floats above the user's pointer. */
                        try {
                          e.dataTransfer.setDragImage(clone, w / 2, h + 24);
                        } catch { /* ignore browsers that reject it */ }
                        window.setTimeout(() => clone.remove(), 0);
                      }
                    }}
                    onDragEnter={() => setOverIndex(i)}
                    onDragOver={(e) => { e.preventDefault(); setOverIndex(i); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (dragIndex !== null) onReorder(dragIndex, i);
                      setDragIndex(null);
                      setOverIndex(null);
                    }}
                    onDragEnd={() => {
                      setDragIndex(null);
                      setOverIndex(null);
                      setCurrentAssetDrag(null);
                    }}
                  >
                    <div className="wizard-upload-order" aria-hidden>{i + 1}</div>
                    <div className="wizard-upload-thumb">
                      {/* draggable={false} on the inner img/video so
                          HTML5 doesn't start its own "drag this image
                          URL" gesture when the user grabs the
                          thumbnail — the tile's outer drag (with our
                          custom MIME) is what we want firing. */}
                      {a.kind === 'image' && a.file_url && (
                        <img
                          alt={a.original_filename}
                          src={a.file_url}
                          draggable={false}
                        />
                      )}
                      {a.kind === 'video' && a.file_url && (
                        <video
                          src={a.file_url}
                          muted
                          preload="metadata"
                          playsInline
                          draggable={false}
                        />
                      )}
                      {a.kind === 'audio' && <div className="wizard-upload-audio">audio</div>}
                      <span className="wizard-upload-kind">{a.kind}</span>
                    </div>
                    <div className="wizard-upload-meta">
                      <span className="wizard-upload-name" title={a.original_filename}>
                        {a.original_filename || `asset #${a.id}`}
                      </span>
                    </div>
                    <div className="wizard-upload-actions">
                      <button
                        type="button"
                        className="ghost btn--icon-sm"
                        onClick={() => onReorder(i, i - 1)}
                        disabled={i === 0}
                        aria-label="Move up"
                      >↑</button>
                      <button
                        type="button"
                        className="ghost btn--icon-sm"
                        onClick={() => onReorder(i, i + 1)}
                        disabled={i === assets.length - 1}
                        aria-label="Move down"
                      >↓</button>
                      <button
                        type="button"
                        className="secondary btn--sm"
                        onClick={() => onAddClip(a)}
                        title="Add this asset as a clip on the timeline"
                      >
                        + Clip
                      </button>
                      <button
                        type="button"
                        className="ghost btn--sm"
                        onClick={() => onDelete(a.id)}
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
